import * as React from "react";
import { createContext, useContext } from "react";
import { IBusyActions, IBusyState, useBusyState } from "../../../components/hooks/use-busy-state";
import { IFormSelectOption } from "./Constants";
import { IWaMembershipFormProps, MonthlyPaymentOption } from "./WaMembershipForm";

interface IWaMembershipFormContext {
	totalDue: number;
	setTotalDue: React.Dispatch<React.SetStateAction<number>>;
	years: IYearsData[];
	stepNum: number;
	setStepNum: (step: number) => void;
	stepImmediateNum: number;
	setStepImmediateNumber: React.Dispatch<React.SetStateAction<number>>;
	step0Class: string;
	step1Class: string;
	step2Class: string;
	step3Class: string;
	step4Class: string;
	submitError: string;
	setSubmitError: React.Dispatch<React.SetStateAction<string>>;
	submitDisabled: boolean;
	setSubmitDisabled: React.Dispatch<React.SetStateAction<boolean>>;
	submitClass: string;
	setSubmitClass: React.Dispatch<React.SetStateAction<string>>;
	backDisabled: boolean;
	setBackDisabled: React.Dispatch<React.SetStateAction<boolean>>;
	backClass: string;
	setBackClass: React.Dispatch<React.SetStateAction<string>>;
	paymentInputComplete: boolean;
	setPaymentInputComplete: React.Dispatch<React.SetStateAction<boolean>>;
	ccData: ICCDate;
	setCCdata: React.Dispatch<React.SetStateAction<ICCDate>>;
	busyState: IBusyState;
	busyStateActions: IBusyActions;
	colorTheme: string;
	formSubmissionRedirectUrl?: string;
	localBranchList: IFormSelectOption[];
	directBranchList: IFormSelectOption[];
	unionList: IFormSelectOption[];
	monthlyPaymentOptions: MonthlyPaymentOption[];
	yearlyPaymentAmount: number;
	concessionCardDiscount: number;
	unionDiscount: number;
	paypalClientId: string;
}

interface IYearsData {
	value: string;
	text: string;
}

interface ICCDate {
	number: string;
	expiry: string;
	cvc: string;
}

const WaMembershipFormContext = createContext<IWaMembershipFormContext | undefined>(undefined);

export function useWaMembershipFormContext() {
	const context = useContext(WaMembershipFormContext);
	if (!context) {
		throw new Error("WaMembershipFormContext is not available.");
	}

	return context;
}

export const WaMembershipFormContextProvider = (props: IWaMembershipFormProps & {children}) => {
	const colorTheme = "red";
	const [totalDue, setTotalDue] = React.useState<number>(props.yearlyPaymentAmount);
	const [years, setYears] = React.useState<IYearsData[]>([{ value: "", text: "" }]);
	const [stepNum, setStepNumber] = React.useState(0);
	const [stepImmediateNum, setStepImmediateNumber] = React.useState(0);
	const [step0Class, setStep0Class] = React.useState("in");
	const [step1Class, setStep1Class] = React.useState("outRight");
	const [step2Class, setStep2Class] = React.useState("outRight");
	const [step3Class, setStep3Class] = React.useState("outRight");
	const [step4Class, setStep4Class] = React.useState("outRight");
	const [submitError, setSubmitError] = React.useState("");
	const [submitDisabled, setSubmitDisabled] = React.useState(true);
	const [submitClass, setSubmitClass] = React.useState("");
	const [backDisabled, setBackDisabled] = React.useState(false);
	const [backClass, setBackClass] = React.useState(`${colorTheme}HoverButton`);
	const [paymentInputComplete, setPaymentInputComplete] = React.useState(false);
	const [ccData, setCCdata] = React.useState<ICCDate>({
		number: "",
		expiry: "",
		cvc: "",
	});
	const [busyState, busyStateActions] = useBusyState();
	const setStepNum = (step: number) => {
		setStepImmediateNumber(step);
		switch (step) {
			case 0:
				if (stepNum === 0) {
					setStep1Class("outRight");
					setStep0Class("in");
				} else if (stepNum === 1) {
					setStep0Class("in");
					setStep1Class("outRight");
				}
				break;
			case 1:
				if (stepNum === 0) {
					setStep0Class("outLeft");
					setStep1Class("in");
				} else if (stepNum === 2) {
					setStep2Class("outRight");
					setStep1Class("in");
				}
				break;
			case 2:
				if (stepNum === 1) {
					setStep1Class("outLeft");
					setStep2Class("in");
				} else if (stepNum === 3) {
					setStep2Class("in");
					setStep3Class("outRight");
				}
				break;
			case 3:
				setStep2Class("outLeft");
				setStep3Class("in");
				break;
			case 4:
				setStep3Class("outLeft");
				setStep4Class("in");
		}
		setTimeout(() => setStepNumber(step), 200);
	};

	React.useEffect(() => {
		let year = new Date().getFullYear();
		const years = [{ value: "", text: "--year--" }];
		for (let i = year; i > year - 100; i--)
			years.push({
				value: i.toString(),
				text: i.toString(),
			});
		setYears(years);
	}, []);

	return (
		<WaMembershipFormContext.Provider
			value={{
				totalDue,
				setTotalDue,
				years,
				stepNum,
				setStepNum,
				stepImmediateNum,
				setStepImmediateNumber,
				step0Class,
				step1Class,
				step2Class,
				step3Class,
				step4Class,
				submitError,
				setSubmitError,
				submitDisabled,
				setSubmitDisabled,
				submitClass,
				setSubmitClass,
				backDisabled,
				setBackDisabled,
				backClass,
				setBackClass,
				paymentInputComplete,
				setPaymentInputComplete,
				ccData,
				setCCdata,
				busyState,
				busyStateActions,
				colorTheme,
				...props
			}}
		>
			{props.children}
		</WaMembershipFormContext.Provider>
	);
};
